import { useEffect, useRef, useCallback } from 'react';
import { useAuthStore } from '@/stores/useAuthStore';
import { authService } from '@/services/AuthService';
import { triggerJitProvisioning } from '@/utils/jitProvision';
import { logger } from '@equip/utils/logger';

// Periodic background refresh interval: pick up CIAM role/profile changes
// without waiting for the token to expire.
const PERIODIC_REFRESH_MS = 15 * 60 * 1000; // 15 minutes

/**
 * Silently re-authenticates every 15 minutes while the user is logged in.
 *
 * Prefers iframe-based silent re-auth (`prompt=none`) because it re-runs the
 * full OIDC broker flow through Keycloak back to Azure CIAM, producing a fresh
 * JWT with up-to-date `realm_access.roles`.  Falls back to the standard
 * refresh-token grant when the SSO session has expired (e.g. after a browser
 * restart) so the access token continues to be renewed for as long as the
 * refresh token remains valid.
 *
 * Must be called from a component that is always mounted while the user is
 * authenticated (e.g. MainLayout).
 */
export const useTokenRefresh = (): void => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const setTokens = useAuthStore((state) => state.setTokens);
  const refreshTokens = useAuthStore((state) => state.refreshTokens);
  const isRefreshing = useRef(false);

  const doRefresh = useCallback(async () => {
    // Read auth state from the store at call time to avoid stale closures
    // when the interval fires after a logout/login cycle.
    if (!useAuthStore.getState().isAuthenticated || isRefreshing.current) return;
    isRefreshing.current = true;
    try {
      // Attempt silent re-auth first so CIAM roles are re-evaluated.
      const tokens = await authService.silentReauth();
      if (tokens) {
        await setTokens(tokens);
        // Fire-and-forget JIT provisioning so the DB record reflects any
        // profile/role changes from the refreshed token.  Note: the
        // refreshTokens() fallback path triggers its own JIT call inside
        // the auth store, so only the successful silent-reauth branch
        // needs an explicit call here.
        const { accessToken } = useAuthStore.getState();
        if (accessToken) {
          triggerJitProvisioning(accessToken);
        }
      } else {
        // SSO session expired — fall back to refresh-token grant so the user
        // stays logged in until the refresh token itself expires.
        logger.debug('Silent reauth unavailable, falling back to token refresh', {
          component: 'useTokenRefresh',
        });
        await refreshTokens();
      }
    } catch (err) {
      logger.warn('Periodic token refresh failed', {
        component: 'useTokenRefresh',
        error: String(err),
      });
    } finally {
      isRefreshing.current = false;
    }
  }, [setTokens, refreshTokens]);

  // Periodic refresh every 15 minutes
  useEffect(() => {
    if (!isAuthenticated) return;

    const id = setInterval(() => {
      logger.debug('Periodic token refresh triggered', {
        component: 'useTokenRefresh',
      });
      void doRefresh();
    }, PERIODIC_REFRESH_MS);

    return () => clearInterval(id);
  }, [isAuthenticated, doRefresh]);
};
